import React from 'react';
import { createRemoteAppComponent } from '@module-federation/bridge-react';
import { init, loadRemote } from '@module-federation/runtime';

import { DefaultLoader } from 'components/default-loader';

/*
 * ⭐ THE ENTIRE MODULE FEDERATION SURFACE OF THIS APP LIVES IN THIS FILE, and that is deliberate.
 *
 * Jest 27 cannot load these packages at all: `transformIgnorePatterns` excludes everything under
 * node_modules and `@module-federation/*` ships ESM, so a bare import throws "Cannot use import
 * statement outside a module". Confining them here means every spec mocks ONE host module instead of
 * two third-party packages, and the rest of the step stays testable.
 *
 * ⚠️ The precedent is `src/floorplan/.../floorplan.spec.tsx`, which mocks `@archilogic/floor-plan-sdk`
 * the same way. Note `resetMocks: true` is set in this repo's jest config, so a `jest.fn()` component
 * returned from a mock factory is wiped before every test and React then renders `undefined` — set the
 * implementation in `beforeEach`, or return a plain function.
 */

const REMOTE_NAME = 'formsAdmin';

/*
 * ⛔ The STATELESS entry. `./widgets/form-renderer-stateful` is a different module whose `readOnly`
 * defaults the OPPOSITE way, so anything learned from a stateful example is inverted here.
 */
const REMOTE_MODULE = `${REMOTE_NAME}/widgets/form-renderer`;

/*
 * ⚠️ Environment-keyed, not a constant. Both `forms-admin.cdn.hqoapp.com` and `forms-admin.hqo.dev`
 * serve a live, genuinely distinct build — their remoteEntry hashes differ — so pointing every
 * environment at one of them would have preprod loading production's renderer.
 *
 * Registered by MANIFEST rather than remoteEntry: forms-admin's rsbuild config sets no `filename`, and
 * `mf-manifest.json` is what its own hosts load.
 */
const remoteEntry = `${process.env.REACT_APP_FORMS_ADMIN_URL ?? ''}/mf-manifest.json`;

/*
 * RES-1336 — the second remote on this host: billing-admin's payment widget, the same one the tenant
 * Service Request app loads. It lives in THIS file because the whole federation surface is confined
 * here (see the note at the top), and because `init` must run once with every remote in one call.
 *
 * ⚠️ Same rules as the form renderer: environment-keyed base URL (production serves
 * `billing-admin.cdn.hqoapp.com`, preprod and development `billing-admin.hqo.dev` — distinct builds),
 * registered by `mf-manifest.json`, and no `shared` entry for React (billing-admin publishes it as
 * `react18`/`reactDom18`, which this host does not declare, so the remote loads its own copy).
 */
const PAYMENT_REMOTE_NAME = 'billingAdmin';
const PAYMENT_REMOTE_MODULE = `${PAYMENT_REMOTE_NAME}/widgets/payment-widget`;
const paymentRemoteEntry = `${process.env.REACT_APP_BILLING_ADMIN_URL ?? ''}/mf-manifest.json`;

let isInitialised = false;

const initialiseRemote = (): void => {
  if (isInitialised) {
    return;
  }

  /*
   * ⛔ NO `shared` ENTRY FOR REACT, and leaving it out is the working configuration rather than an
   * omission. forms-admin publishes React under the share keys `react18`/`reactDom18`; this host
   * registers nothing, so the scopes do not intersect and the remote loads its own copy — which is how
   * the existing tenant-facing consumer runs today. Declaring `react` here would NOT dedupe (different
   * key) but would introduce a singleton negotiation that can only fail.
   */
  init({
    name: 'resourceBooking',
    remotes: [
      { name: REMOTE_NAME, entry: remoteEntry },
      { name: PAYMENT_REMOTE_NAME, entry: paymentRemoteEntry },
    ],
  });
  isInitialised = true;
};

interface RemoteFailureProps {
  error: Error;
}

/*
 * ⛔ THIS FALLBACK IS WHY A FAILED REMOTE DOES NOT TAKE THE APP DOWN.
 *
 * `PaymentContent` mounts under the landing route, which is a STATIC import with no Suspense or error
 * boundary of its own — so without a boundary here, a remote that fails to load falls through to the
 * app-level `Sentry.ErrorBoundary` in `root.tsx` and drops the whole app to the generic error page.
 * At this point in the flow the tenant has already paid for the room; losing their receipt to a CDN
 * blip on an optional form is the worst outcome available.
 *
 * Rendering nothing is correct: the step fails open everywhere else too, so a tenant whose form cannot
 * load simply submits without answers, exactly as the majority with no vendor form do.
 */
const RemoteFailure = ({ error }: RemoteFailureProps): JSX.Element | null => {
  // eslint-disable-next-line no-console
  console.warn('RES-1057: the vendor form renderer failed to load; continuing without it', error);

  return null;
};

export const createFormRendererRemote = () => {
  initialiseRemote();

  return createRemoteAppComponent({
    loader: () => loadRemote(REMOTE_MODULE) as Promise<Record<string, unknown>>,
    loading: <DefaultLoader />,
    fallback: RemoteFailure,
  });
};

/*
 * ⛔ NOT THE SILENT FALLBACK ABOVE. A form that fails to load can be skipped; a payment cannot — rendering
 * nothing would leave a tenant on a screen with no way to pay and no explanation. The caller supplies the
 * failure UI (it owns the copy and the "pay later" exit), so this only forwards the error to it.
 */
export const createPaymentWidgetRemote = (fallback: React.ComponentType<{ error: Error }>) => {
  initialiseRemote();

  return createRemoteAppComponent({
    loader: () => loadRemote(PAYMENT_REMOTE_MODULE) as Promise<Record<string, unknown>>,
    loading: <DefaultLoader />,
    fallback,
  });
};
